import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeWorkGroupsExportPayload,
  ChangeWorkGroupsImportResponse,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';

interface Props {
  show: boolean;
  onHide: () => void;
  onImported: () => void;
}

const ChangeWorkGroupsImportExportModal: React.FC<Props> = ({ show, onHide, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ChangeWorkGroupsImportResponse | null>(null);

  const reset = () => {
    setFile(null);
    setDryRun(true);
    setError(null);
    setSuccess(null);
    setImportResult(null);
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const ext = selected.name.toLowerCase().split('.').pop() || '';
    if (ext !== 'json') {
      setError('Selecione um arquivo JSON exportado do cadastro de grupos de mudanças.');
      setFile(null);
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setError('O arquivo excede 5 MB.');
      setFile(null);
      return;
    }
    setFile(selected);
    setError(null);
    setSuccess(null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!file) {
      setError('Selecione um arquivo JSON.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as ChangeWorkGroupsExportPayload;
      if (!parsed || parsed.module !== 'changes_work_groups' || !Array.isArray(parsed.groups)) {
        throw new Error(
          'Arquivo inválido: esperado module «changes_work_groups» e lista «groups».',
        );
      }
      const result = await changesApi.importWorkGroups(
        {
          version: parsed.version || '1.0',
          module: parsed.module,
          groups: parsed.groups,
        },
        dryRun,
      );
      setImportResult(result);
      if (result.errors > 0) {
        setError(`Importação finalizada com ${result.errors} erro(s).`);
      }
      setSuccess(
        dryRun
          ? `Validação: ${result.created} criação(ões), ${result.updated} atualização(ões).`
          : `Concluído: ${result.created} criado(s), ${result.updated} atualizado(s).`,
      );
      if (!dryRun && result.errors === 0) {
        onImported();
      }
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível processar o arquivo JSON.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar grupos de mudanças</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          O JSON deve ser exportado deste cadastro (Gestão de Mudanças → Grupos). Membros são
          vinculados por e-mail de usuário ativo; subtarefas internas por nome do modelo global;
          chamados externos por nome do grupo e do fluxo no módulo de Incidentes.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Arquivo JSON</Form.Label>
          <Form.Control type="file" accept=".json,application/json" onChange={handleFileChange} />
        </Form.Group>
        <Form.Check
          type="checkbox"
          id="chg-wg-import-dry-run"
          className="mb-3"
          label="Somente validar (dry run) — não grava alterações"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {success ? <Alert variant="success">{success}</Alert> : null}
        {importResult?.results?.length ? (
          <div className="table-responsive" style={{ maxHeight: 240, overflowY: 'auto' }}>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th>Ação</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {importResult.results.map((row, index) => (
                  <tr key={`${row.name}-${index}`}>
                    <td>{row.name}</td>
                    <td>{row.action}</td>
                    <td className="small text-muted">{row.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
        <Button variant="primary" onClick={() => void handleImport()} disabled={!file || loading}>
          {loading ? 'Processando...' : dryRun ? 'Validar' : 'Importar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeWorkGroupsImportExportModal;
